import React, { useState } from "react";
import { FileText, RefreshCw, ExternalLink, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * One-tap ads.txt sync from Ezoic's ads.txt Manager.
 * The generated file is served at https://fliboe.com/ads.txt (frontend public/).
 */
export default function AdsTxtSyncCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const sync = async () => {
    setBusy(true); setResult(null); setError("");
    try {
      const { data } = await api.post("/admin/ads-txt/sync", {}, { headers: authHeaders() });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="fl-card p-6 mt-6" data-testid="ads-txt-sync-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-400/30 flex items-center justify-center">
            <FileText className="w-5 h-5 text-sky-400" />
          </div>
          <div>
            <h2 className="fl-display text-xl font-bold text-white">ads.txt sync (Ezoic)</h2>
            <p className="text-white/50 text-sm mt-0.5">
              Pulls the latest publisher lines from Ezoic&apos;s ads.txt Manager into <code className="text-emerald-300">/ads.txt</code>.
              Run this once after Ezoic activates your domain, then once a month.
            </p>
          </div>
        </div>
        <button onClick={sync} disabled={busy} className="fl-btn" data-testid="ads-txt-sync-btn">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Syncing…" : "Sync ads.txt"}
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
        <a
          href="https://fliboe.com/ads.txt"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-white/60 hover:text-white"
          data-testid="ads-txt-view-link"
        >
          <ExternalLink className="w-3.5 h-3.5" /> View current fliboe.com/ads.txt
        </a>
      </div>

      {result && (
        <div className="mt-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-400/30 text-emerald-200 text-sm flex items-start gap-2" data-testid="ads-txt-sync-result">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            Synced {result.lines} publisher line{result.lines === 1 ? "" : "s"} ({result.bytes} bytes) from Ezoic.
            <div className="text-emerald-300/70 text-xs mt-0.5">
              Source: <code>{result.source}</code>
            </div>
          </div>
        </div>
      )}
      {error && (
        <div className="mt-4 p-3 rounded-xl bg-red-500/10 border border-red-400/30 text-red-200 text-sm flex items-start gap-2" data-testid="ads-txt-sync-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>{error}</div>
        </div>
      )}
    </section>
  );
}
